import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { CreditCard, CheckCircle2, ArrowUpRight, Loader2, ExternalLink, AlertCircle } from 'lucide-react';
import { billingAPI } from '../../lib/api';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const FREE_PLAN = {
  id: 'free',
  name: 'Free',
  price: 0,
  currency: 'GBP',
  interval: 'month',
  features: ['Limited articles', 'Basic reading', 'Community access'],
};

const CURRENCY_SYMBOLS = { GBP: '£', USD: '$', EUR: '€' };

function formatCurrency(amount, currency = 'GBP') {
  const sym = CURRENCY_SYMBOLS[currency?.toUpperCase()] || currency;
  return `${sym}${Number(amount).toFixed(2)}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return dateStr; }
}

export default function BillingSetting() {
  const [packages, setPackages] = useState([]);
  const [currentTier, setCurrentTier] = useState('free');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    setLoading(true);
    setHistoryLoading(true);
    try {
      const [pricesRes, tierRes] = await Promise.all([
        billingAPI.prices().catch(() => ({ data: { packages: [] } })),
        billingAPI.userTier().catch(() => ({ data: { tier: 'free' } })),
      ]);
      setPackages(pricesRes.data.packages || []);
      setCurrentTier(tierRes.data.tier || 'free');
    } catch {
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
    try {
      const histRes = await billingAPI.history();
      setTransactions(histRes.data.transactions || []);
    } catch {
      // Silently fail — user may have no history
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleCheckout = async (pkg) => {
    const id = pkg.id;
    setCheckingOut(id);
    try {
      const res = await billingAPI.checkout(id);
      const url = res.data.url;
      if (url) {
        window.open(url, '_blank');
        toast.success('Stripe checkout opened in a new tab');
      } else {
        toast.error('No checkout URL returned');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to start checkout';
      toast.error(msg);
    } finally {
      setCheckingOut(null);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const res = await billingAPI.portal(window.location.href);
      const url = res.data.url;
      if (url) {
        window.open(url, '_blank');
      } else {
        toast.error('Could not open billing portal');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'No active subscription found. Complete a checkout first.';
      toast.error(msg);
    } finally {
      setPortalLoading(false);
    }
  };

  const allPlans = [FREE_PLAN, ...packages];

  const statusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'paid' || s === 'complete') return 'text-green-600 border-green-200 bg-green-50';
    if (s === 'pending') return 'text-amber-600 border-amber-200 bg-amber-50';
    return 'text-gray-600 border-gray-200 bg-gray-50';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-40 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Plans */}
      <Card className="bg-white dark:bg-[#1A1D23] border-[#E7E0D6] dark:border-white/[0.06]">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-[#B89B5E]" /> Subscription Plans
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={cn('grid gap-4', allPlans.length <= 3 ? 'grid-cols-1 md:grid-cols-3' : `grid-cols-1 md:grid-cols-${Math.min(allPlans.length, 4)}`)}>
            {allPlans.map(plan => {
              const isCurrent = plan.id === currentTier;
              const sym = CURRENCY_SYMBOLS[plan.currency?.toUpperCase()] || plan.currency || '£';
              return (
                <div
                  key={plan.id}
                  className={cn(
                    'rounded-xl border-2 p-5 transition-all relative',
                    isCurrent
                      ? 'border-[#B89B5E] bg-[#B89B5E]/5 ring-2 ring-[#B89B5E]/20'
                      : 'border-[#E7E0D6] dark:border-white/10 hover:border-[#B89B5E]/40'
                  )}
                >
                  {isCurrent && (
                    <Badge className="absolute -top-2.5 right-3 bg-[#B89B5E] text-white text-[10px]">Current</Badge>
                  )}
                  <h3 className="text-lg font-semibold dark:text-white/90">{plan.name}</h3>
                  <div className="flex items-baseline gap-0.5 mt-2">
                    <span className="text-3xl font-bold dark:text-white/90">{sym}{Number(plan.price).toFixed(2)}</span>
                    <span className="text-sm text-[#5B616E] dark:text-white/40">/{plan.interval || 'month'}</span>
                  </div>
                  {plan.features && (
                    <ul className="mt-4 space-y-2">
                      {plan.features.map(f => (
                        <li key={f} className="flex items-center gap-2 text-xs text-[#5B616E] dark:text-white/50">
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#1F7A5A] flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                  {plan.id === 'free' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled
                      className="w-full mt-4 text-xs opacity-60"
                    >
                      {isCurrent ? 'Current Plan' : 'Free Tier'}
                    </Button>
                  ) : (
                    <Button
                      variant={isCurrent ? 'outline' : 'default'}
                      size="sm"
                      disabled={isCurrent || checkingOut === plan.id}
                      className={cn('w-full mt-4 text-xs', isCurrent && 'pointer-events-none opacity-60')}
                      onClick={() => !isCurrent && handleCheckout(plan)}
                    >
                      {checkingOut === plan.id ? (
                        <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Processing...</>
                      ) : isCurrent ? (
                        'Current Plan'
                      ) : (
                        <>Upgrade <ArrowUpRight className="w-3 h-3 ml-1" /></>
                      )}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Payment Method / Stripe Portal */}
      <Card className="bg-white dark:bg-[#1A1D23] border-[#E7E0D6] dark:border-white/[0.06]">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Payment Method</CardTitle>
        </CardHeader>
        <CardContent>
          {currentTier === 'free' ? (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-[#FBF7EF] dark:bg-white/[0.04] border border-[#E7E0D6] dark:border-white/[0.06]">
              <AlertCircle className="w-5 h-5 text-[#5B616E] flex-shrink-0" />
              <div>
                <p className="text-sm font-medium dark:text-white/80">No payment method</p>
                <p className="text-xs text-[#5B616E] dark:text-white/40">Subscribe to a plan to add a payment method via Stripe</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between p-4 rounded-lg bg-[#FBF7EF] dark:bg-white/[0.04] border border-[#E7E0D6] dark:border-white/[0.06]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-8 rounded bg-gradient-to-r from-[#1E5A8A] to-[#1E5A8A]/80 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium dark:text-white/80">Managed by Stripe</p>
                  <p className="text-xs text-[#5B616E] dark:text-white/40">View and update your payment details in the Stripe portal</p>
                </div>
              </div>
              <Button variant="outline" size="sm" className="text-xs" onClick={handleManageBilling} disabled={portalLoading}>
                {portalLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ExternalLink className="w-3 h-3 mr-1" />}
                {portalLoading ? 'Opening...' : 'Manage'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Billing History */}
      <Card className="bg-white dark:bg-[#1A1D23] border-[#E7E0D6] dark:border-white/[0.06]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium">Billing History</CardTitle>
            {transactions.length > 0 && (
              <Badge variant="secondary" className="text-[10px]">{transactions.length} transaction{transactions.length !== 1 ? 's' : ''}</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => <Skeleton key={i} className="h-12 rounded-lg" />)}
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8">
              <CreditCard className="w-8 h-8 text-[#5B616E]/20 dark:text-white/10" />
              <p className="text-sm text-[#5B616E] dark:text-white/40">No transactions yet</p>
              <p className="text-xs text-[#5B616E]/70 dark:text-white/25">Transactions will appear here after your first payment</p>
            </div>
          ) : (
            <div className="space-y-0 divide-y divide-[#E7E0D6] dark:divide-white/[0.06]">
              {transactions.map((tx, i) => (
                <div key={tx.id || tx.session_id || i} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-medium dark:text-white/80">
                      {tx.package_name || tx.description || tx.package_id || 'Payment'}
                    </p>
                    <p className="text-xs text-[#5B616E] dark:text-white/40">{formatDate(tx.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium dark:text-white/80">
                      {formatCurrency(tx.amount, tx.currency)}
                    </span>
                    <Badge variant="outline" className={cn('text-[10px]', statusColor(tx.status || tx.payment_status))}>
                      {(tx.status || tx.payment_status || 'unknown').replace(/_/g, ' ')}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
